/**
 * The home page's live parts: "Next up" rolls over to the following event
 * when one starts, the countdown ticks every minute, and each competition
 * card keeps showing its next fixtures, in the viewer's own time zone.
 * Competition pages share the Next up card's rendering (renderNext).
 *
 * The page embeds the data it was rendered from (#upcoming-data,
 * #series-data). It is re-fetched from /api/upcoming.json when the list of
 * next events runs low, and whenever a fixture on a card starts, since each
 * card only holds the next few events of its competition.
 */
import type { UpcomingEvent, UpcomingSummary } from "../core/upcoming.ts";
import { localDate, localTime } from "./times.ts";

interface SeriesInfo {
  name: string;
  shortName: string;
  color: string;
  /** Black or white: the band's text on the colour. */
  ink: string;
  logo: string;
  /** Id of the series' subscribe popup. */
  popup: string;
}

/** Fetch more events once fewer than this many are left to roll through. */
const REFILL_BELOW = 10;
/** How many fixtures a card shows; matches the server's. */
const SLOTS = 2;

const displayTitle = (text: string): string => text.toUpperCase().replace(/ V /g, " v ");
const pad = (n: number): string => String(n).padStart(2, "0");
const reducedMotion = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** When an event starts. All-day events start at local midnight, wherever the viewer is. */
export function startTime(event: UpcomingEvent): Date {
  if (!event.allDay) return new Date(event.start);
  const [year = 0, month = 1, day = 1] = event.start.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function readJson(id: string): unknown {
  const text = document.getElementById(id)?.textContent;
  return text ? JSON.parse(text) : undefined;
}

/** A new element with a class and, optionally, text. */
function element(tag: string, className: string, text?: string): HTMLElement {
  const created = document.createElement(tag);
  created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

/** Sets an element's text if it changed; true when it did. */
const setText = (target: Element | null, text: string): boolean => {
  if (!target || target.textContent === text) return false;
  target.textContent = text;
  return true;
};

/** An event's start as the page prints it, in local time. */
function when(event: UpcomingEvent): HTMLTimeElement {
  const time = document.createElement("time");
  time.className = "when";
  time.dateTime = event.start;
  const start = startTime(event);
  time.append(element("span", "when-date", localDate(start)));
  if (!event.allDay) time.append(" · ", element("span", "when-time", localTime(start)));
  return time;
}

/** Shows `event` in the Next up card: when, title, label and the time left until it starts. */
export function renderNext(event: UpcomingEvent, now: Date): void {
  const time = document.getElementById("next-time");
  if (time?.querySelector("time")?.dateTime !== event.start) time?.replaceChildren(when(event));
  setText(document.getElementById("next-title"), displayTitle(event.headline));
  setText(document.getElementById("next-subtitle"), event.label);

  const diff = Math.max(0, startTime(event).getTime() - now.getTime());
  setText(document.getElementById("days"), pad(Math.floor(diff / 86_400_000)));
  setText(document.getElementById("hours"), pad(Math.floor((diff % 86_400_000) / 3_600_000)));
  const minutes = document.getElementById("minutes");
  if (setText(minutes, pad(Math.floor((diff % 3_600_000) / 60_000))) && !reducedMotion()) {
    // The new minute rolls down into place.
    minutes?.animate(
      [
        { transform: "translateY(-0.35em)", opacity: 0 },
        { transform: "none", opacity: 1 },
      ],
      { duration: 260, easing: "cubic-bezier(0.23, 1, 0.32, 1)" },
    );
  }
}

/** The home page's Next up: the event, under its competition's band, with its Subscribe button. */
function renderHero(event: UpcomingEvent, series: SeriesInfo, now: Date): void {
  const hero = document.querySelector<HTMLElement>(".next-up");
  if (!hero) return;
  const band = hero.querySelector<HTMLElement>(".band");
  band?.style.setProperty("--c", series.color);
  band?.style.setProperty("--c-ink", series.ink);
  const logo = band?.querySelector("img");
  if (logo && series.logo) logo.src = series.logo;
  setText(document.getElementById("next-series"), series.name);
  const button = document.getElementById("add-btn");
  button?.setAttribute("popovertarget", series.popup);
  setText(button, `Subscribe to ${series.shortName}`);
  renderNext(event, now);
}

/** Runs `tick` now and then as each minute begins, so the minutes change when the clock does. */
export function everyMinute(tick: () => void): void {
  tick();
  const now = new Date();
  window.setTimeout(
    () => {
      tick();
      window.setInterval(tick, 60_000);
    },
    (60 - now.getSeconds()) * 1000 - now.getMilliseconds(),
  );
}

/** Refills a card's fixture slots once one of them has started. */
function renderSlots(list: HTMLOListElement, events: UpcomingEvent[]): void {
  const shown = events.slice(0, SLOTS);
  const current = [...list.querySelectorAll("time")].map((time) => time.dateTime);
  if (current.length === shown.length && current.every((start, i) => start === shown[i]?.start)) return;
  const items = shown.map((event) => {
    const slot = element("li", "slot");
    slot.append(when(event), element("span", "slot-title", event.headline));
    return slot;
  });
  // Only reached once a card's fixtures have all started, so its feed has been read.
  if (items.length === 0) items.push(element("li", "slot slot-none", "No fixtures scheduled"));
  list.replaceChildren(...items);
}

/** Starts the Next up countdown and card updates from the data embedded in the page. */
export function startCountdown(): void {
  let data = readJson("upcoming-data") as UpcomingSummary | undefined;
  const series = readJson("series-data") as Record<string, SeriesInfo> | undefined;
  if (!data || !series) return;
  let refilling = false;

  const tick = (): void => {
    if (!data) return;
    const now = new Date();
    const upcoming = (events: UpcomingEvent[]) => events.filter((e) => startTime(e) >= now);
    const cardEvents = (summary: UpcomingSummary) =>
      Object.values(summary.bySeries).reduce((count, list) => count + list.length, 0);
    const before = cardEvents(data);
    data = {
      next: upcoming(data.next),
      bySeries: Object.fromEntries(
        Object.entries(data.bySeries).map(([slug, list]) => [slug, upcoming(list)]),
      ),
    };
    const cardEventStarted = cardEvents(data) < before;

    const [next] = data.next;
    const info = next ? series[next.series] : undefined;
    if (next && info) renderHero(next, info, now);
    for (const list of document.querySelectorAll<HTMLOListElement>("ol[data-slots]")) {
      renderSlots(list, data.bySeries[list.dataset["slots"] ?? ""] ?? []);
    }

    if ((data.next.length < REFILL_BELOW || cardEventStarted) && !refilling) {
      refilling = true;
      fetch("/api/upcoming.json")
        .then(async (response) => {
          if (response.ok) {
            data = (await response.json()) as UpcomingSummary;
            tick();
          }
        })
        .catch(() => undefined)
        .finally(() => {
          refilling = false;
        });
    }
  };

  everyMinute(tick);
}
